import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { projectsReducer } from '../../store/reducers/projects.reducer.ts';

const { createFolder } = projectsReducer.actions;

interface CreateFolderModalProps {
  folderId: number | undefined;
  isOpen: boolean;
  onClose: () => void;
}

interface CreateFolderPayload {
  folderName: string;
  parentId: number | null;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({ folderId, isOpen, onClose }) => {
  const { t } = useTranslation();
  const [folderName, setFolderName] = useState('');

  const dispatch = useDispatch();
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const folderIdValue = folderId || null;
    if (folderName.trim()) {
      const payload: CreateFolderPayload = {
        folderName: folderName.trim(),
        parentId: folderIdValue,
      };
      dispatch(createFolder(payload));
      setFolderName('');
      onClose();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  const handleCancel = () => {
    setFolderName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
        onClick={handleCancel}
        onKeyDown={handleKeyDown}
      >
        <div className="bg-greyP5 rounded-md shadow-xl w-[602px] h-auto p-10" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between pb-2 border-b border-greyP4">
            <div className="flex items-center space-x-3">
              <h2 className="text-white text-lg font-medium">{t('projects.createFolder.title')}</h2>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="pt-8 space-y-6">
            <div>
              <input
                type="text"
                id="folderName"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                placeholder={t('projects.createFolder.placeholder')}
                className="w-full px-4 py-3 bg-black border border-black rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-green01 transition-colors duration-200"
                autoFocus
              />
            </div>

            <div className="flex items-center justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 hover:text-gray-400 text-white border border-white rounded-3xl hover:border-gray-500 transition-colors duration-200"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={!folderName.trim()}
                className="px-6 py-2 bg-white text-black font-medium rounded-3xl hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {t('projects.createFolder.submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateFolderModal;
